import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import CarteInstallationsCommercial from './CarteInstallationsCommercial';
import {
  Trophy,
  Medal,
  Users,
  FileText,
  CheckCircle,
  Target,
  Phone,
  Calendar,
  UserPlus,
  Flame,
  ArrowUp,
  Sun,
  Cloud,
  CloudSun,
  CloudRain,
  CloudSnow,
  CloudFog,
  Zap,
  Home,
  BellRing
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardCommercial = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meteo, setMeteo] = useState({ temp: '--', ville: 'Croissy-Beaubourg', condition: 'Chargement...', icon: 'sun' });

  // Couleur dynamique pour le taux de transformation (en %)
  const getCouleurTaux = (taux) => {
    const t = parseFloat(taux);
    if (isNaN(t)) return '#6B7280';
    if (t >= 1.5) return '#10B981';
    if (t >= 0.7) return '#F59E0B';
    return '#EF4444';
  };

  // Couleur dynamique pour le delai 1er contact
  const getCouleurDelai = (delai) => {
    if (!delai || delai === '—') return '#6B7280';
    let totalMin = 0;
    const matchH = delai.match(/(\d+)\s*h/);
    const matchMin = delai.match(/(\d+)\s*min/);
    if (matchH) totalMin += parseInt(matchH[1], 10) * 60;
    if (matchMin) totalMin += parseInt(matchMin[1], 10);
    if (totalMin === 0) {
      const onlyNum = delai.match(/(\d+)/);
      if (onlyNum) totalMin = parseInt(onlyNum[1], 10);
    }
    if (totalMin <= 90) return '#10B981';
    if (totalMin <= 180) return '#F59E0B';
    return '#EF4444';
  };

  // Codes meteo Open-Meteo -> condition + icone
  const decodeMeteo = (code) => {
    if (code === 0) return { condition: 'Ensoleille', icon: 'sun' };
    if (code === 1) return { condition: 'Peu nuageux', icon: 'cloudsun' };
    if (code === 2) return { condition: 'Partiellement nuageux', icon: 'cloudsun' };
    if (code === 3) return { condition: 'Nuageux', icon: 'cloud' };
    if (code <= 48) return { condition: 'Brouillard', icon: 'fog' };
    if (code <= 67) return { condition: 'Pluie', icon: 'rain' };
    if (code <= 77) return { condition: 'Neige', icon: 'snow' };
    if (code <= 82) return { condition: 'Averses', icon: 'rain' };
    if (code <= 99) return { condition: 'Orage', icon: 'storm' };
    return { condition: 'Inconnu', icon: 'sun' };
  };

  // Charger la meteo depuis Open-Meteo
  const loadMeteo = async (lat, lon, villeOverride) => {
    try {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&timezone=Europe/Paris`;
      const res = await fetch(url);
      const json = await res.json();
      const temp = Math.round(json.current_weather?.temperature ?? 0);
      const code = json.current_weather?.weathercode ?? 0;
      const { condition, icon } = decodeMeteo(code);
      let ville = villeOverride;
      if (!ville) {
        try {
          const geoUrl = `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${lat}&longitude=${lon}&language=fr&format=json`;
          const geoRes = await fetch(geoUrl);
          const geoJson = await geoRes.json();
          ville = geoJson?.results?.[0]?.name || 'Ma position';
        } catch (e) {
          ville = 'Ma position';
        }
      }
      setMeteo({ temp, ville, condition, icon });
    } catch (e) {
      console.error('Erreur meteo:', e);
      setMeteo({ temp: '--', ville: 'Croissy-Beaubourg', condition: 'Indisponible', icon: 'sun' });
    }
  };

  // Au montage : essayer la geoloc, sinon fallback Croissy-Beaubourg
  useEffect(() => {
    const fallback = () => loadMeteo(48.8167, 2.6333, 'Croissy-Beaubourg');
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => loadMeteo(pos.coords.latitude, pos.coords.longitude),
        () => fallback(),
        { timeout: 5000 }
      );
    } else {
      fallback();
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      loadDashboard();
    }
  }, [user]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/commercial/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement dashboard:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement du dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #E5E7EB', borderTop: '3px solid #1FD1FD', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C', padding: '16px', borderRadius: '12px' }}>
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { commercial, objectif, classement, compteurs, performance, a_faire } = data;
  const totalActions = (a_faire?.rappels_nrp || 0) + (a_faire?.devis_a_relancer || 0) + (a_faire?.nouveaux_leads || 0);

  // Couleur du message motivant selon le type
  const messageColors = {
    success: { bg: '#ECFDF5', border: '#A7F3D0', textPrimary: '#065F46', textSecondary: '#047857', icon: '#10B981' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', textPrimary: '#92400E', textSecondary: '#B45309', icon: '#F59E0B' },
    info: { bg: '#EFF6FF', border: '#BFDBFE', textPrimary: '#1E3A8A', textSecondary: '#1E40AF', icon: '#3B82F6' }
  };
  const msgColor = messageColors[objectif.message_type] || messageColors.info;

  return (
    <div style={{ padding: '24px 0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* OBJECTIF + CLASSEMENT */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '24px' }}>

        {/* Objectif du mois */}
        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Trophy style={{ width: '18px', height: '18px', color: '#1FD1FD' }} />
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Mon objectif du mois</span>
            </div>
            {objectif.record_perso > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#F3F4F6', padding: '4px 10px', borderRadius: '999px' }}>
                <Flame style={{ width: '12px', height: '12px', color: '#F59E0B' }} />
                <span style={{ fontSize: '11px', color: '#6B7280' }}>Record : {objectif.record_perso} pose{objectif.record_perso > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '14px' }}>
            <div style={{ fontSize: '44px', fontWeight: 600, color: '#111827', lineHeight: 1 }}>{objectif.poses_mois}</div>
            <div style={{ fontSize: '16px', color: '#6B7280' }}>/ {objectif.objectif_mois} poses</div>
          </div>

          <div style={{ position: 'relative', height: '10px', background: '#F3F4F6', borderRadius: '999px', overflow: 'hidden', marginBottom: '14px' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${Math.min(objectif.pourcentage, 100)}%`, background: '#1FD1FD', borderRadius: '999px' }}></div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: msgColor.bg, border: `1px solid ${msgColor.border}`, borderRadius: '10px' }}>
            <Flame style={{ width: '22px', height: '22px', color: msgColor.icon, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: msgColor.textPrimary }}>
                {objectif.message_motivant}
              </div>
              <div style={{ fontSize: '11px', color: msgColor.textSecondary, marginTop: '2px' }}>
                {objectif.pourcentage}% de l'objectif atteint
              </div>
            </div>
          </div>
        </div>

        {/* Classement */}
        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '20px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '14px' }}>
            <Medal style={{ width: '16px', height: '16px', color: '#1FD1FD' }} />
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>Mon classement</span>
          </div>
          <div style={{ width: '68px', height: '68px', background: '#FEF3C7', border: '3px solid #FBBF24', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <span style={{ fontSize: '30px', fontWeight: 700, color: '#B45309' }}>{classement.position || '—'}</span>
          </div>
          <div style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
            sur {classement.total} commerciaux
          </div>
        </div>
      </div>

      {/* COMPTEURS - PROSPECTS / DEVIS / SIGNÉS / INSTALLÉS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>

        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '18px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '36px', fontWeight: 600, color: '#111827', lineHeight: 1 }}>{compteurs.mes_prospects}</div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '8px' }}>Mes prospects</div>
            </div>
            <div style={{ width: '36px', height: '36px', background: '#ECFDF5', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users style={{ width: '18px', height: '18px', color: '#10B981' }} />
            </div>
          </div>
        </div>

        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '18px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '36px', fontWeight: 600, color: '#111827', lineHeight: 1 }}>{compteurs.devis_envoyes}</div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '8px' }}>Devis envoyés</div>
            </div>
            <div style={{ width: '36px', height: '36px', background: '#FEF3C7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText style={{ width: '18px', height: '18px', color: '#F59E0B' }} />
            </div>
          </div>
        </div>

        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '18px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '36px', fontWeight: 600, color: '#111827', lineHeight: 1 }}>{compteurs.devis_signes}</div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '8px' }}>Devis signés</div>
            </div>
            <div style={{ width: '36px', height: '36px', background: '#DBEAFE', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle style={{ width: '18px', height: '18px', color: '#3B82F6' }} />
            </div>
          </div>
        </div>

        <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '18px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '36px', fontWeight: 600, color: '#111827', lineHeight: 1 }}>{compteurs.clients_installes || 0}</div>
              <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '8px' }}>Clients installés</div>
            </div>
            <div style={{ width: '36px', height: '36px', background: '#EDE9FE', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Home style={{ width: '18px', height: '18px', color: '#8B5CF6' }} />
            </div>
          </div>
        </div>

      </div>

      {/* À FAIRE AUJOURD'HUI */}
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '20px 24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BellRing style={{ width: '18px', height: '18px', color: '#1FD1FD' }} />
            <span style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>À faire aujourd'hui</span>
          </div>
          <span style={{ background: '#FEF2F2', color: '#B91C1C', padding: '4px 12px', borderRadius: '999px', fontSize: '11px', fontWeight: 600 }}>
            {totalActions} action{totalActions > 1 ? 's' : ''}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          <div style={{ padding: '14px 16px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#1E40AF', marginBottom: '8px' }}>
              <UserPlus style={{ width: '14px', height: '14px' }} />Nouveaux prospects
            </div>
            <div style={{ fontSize: '28px', fontWeight: 600, color: '#1E3A8A' }}>{a_faire.nouveaux_leads}</div>
          </div>
          <div style={{ padding: '14px 16px', background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#5B21B6', marginBottom: '8px' }}>
              <FileText style={{ width: '14px', height: '14px' }} />Devis envoyés
            </div>
            <div style={{ fontSize: '28px', fontWeight: 600, color: '#6D28D9' }}>{a_faire.devis_a_relancer}</div>
          </div>
          <div style={{ padding: '14px 16px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#92400E', marginBottom: '8px' }}>
              <Phone style={{ width: '14px', height: '14px' }} />Rappels NRP
            </div>
            <div style={{ fontSize: '28px', fontWeight: 600, color: '#B45309' }}>{a_faire.rappels_nrp}</div>
          </div>
        </div>
      </div>
      {/* MA PERFORMANCE */}
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '16px', padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
          <Target style={{ width: '18px', height: '18px', color: '#1FD1FD' }} />
          <span style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Ma performance</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 600, color: getCouleurTaux(performance.taux_transformation), lineHeight: 1 }}>{performance.taux_transformation}%</div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>Taux de transformation</div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 600, color: getCouleurDelai(performance.delai_premier_contact), lineHeight: 1 }}>{performance.delai_premier_contact}</div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>Délai 1er contact</div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 600, color: '#3B82F6', lineHeight: 1 }}>{performance.panier_moyen}</div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>Panier moyen</div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 600, color: '#1FD1FD', lineHeight: 1 }}>{performance.puissance_installee}</div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>Puissance installée totale</div>
          </div>
        </div>
      </div>



      {/* Carte de France : uniquement les poses du commercial connecte */}
      <div style={{ marginTop: '16px' }}>
        <CarteInstallationsCommercial />
      </div>
    </div>
  );
};

export default DashboardCommercial;
